<template>
  <!-- A field with things inside its frame: an icon on the left, a button on
       the right, one ring around all of it that lights up while the input has
       focus. The generator's URL field is the original; the extension page's
       hand-off, the brand search, the API sign-up and the dashboard key use the
       same control so they read as one control in several places.

       Attributes (id, type, placeholder, readonly, listeners…) land on the
       <input>; `class` and `style` stay on the frame. `elevated` is for a field
       floating over scrolling content. -->
  <div
    class="flex w-full items-center rounded-md ring-1 ring-inset transition focus-within:ring-2 focus-within:ring-inset"
    :class="[
      invalid ? 'ring-red-700 focus-within:ring-red-700' : 'ring-gray-700 focus-within:ring-indigo-600',
      elevated ? 'bg-gray-900/95 shadow-2xl shadow-black/60 backdrop-blur' : 'bg-gray-900 shadow-sm',
      $attrs.class,
    ]"
    :style="$attrs.style"
  >
    <span v-if="$slots.leading" class="ml-2.5 flex size-6 shrink-0 items-center justify-center">
      <slot name="leading" />
    </span>
    <input
      v-model="model"
      v-bind="withoutFrameAttrs($attrs)"
      class="block w-full min-w-0 flex-1 border-0 bg-transparent px-2 py-2.5 text-base text-white placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
      :class="inputClass"
      :aria-invalid="invalid || undefined"
    />
    <slot name="trailing" />
  </div>
</template>

<script setup>
defineOptions({ inheritAttrs: false })

const model = defineModel({ type: String, default: '' })

defineProps({
  invalid: { type: Boolean, default: false },
  elevated: { type: Boolean, default: false },
  // Extra classes for the <input> itself (e.g. a monospaced value).
  inputClass: { type: String, default: '' },
})

// Split at render time: $attrs isn't reactive, so a computed over it would
// miss a changed `type` or `placeholder`.
const withoutFrameAttrs = ({ class: _class, style: _style, ...rest }) => rest
</script>
